import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";
import MobileNav from "../../components/mobileNav/MobileNav";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserPermissions } from "../../reduxSlices/userSlice";
import PrintingHeader from "../printingHeader/PrintingHeader";
import printIcon from "../../assets/print-icon.svg";
import SearchInput from "../searchBar/SearchBar";
import { useMediaQuery } from "react-responsive";
import ReactPaginate from "react-paginate";
import {
  FaCalendarAlt,
  FaUserCog,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaTrash,
  FaClock,
} from "react-icons/fa";
import "./waiting.css";

const ApprovedList = (props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notePopUpRequestId, setNotePopUpRequestId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    MaintenanceTypeId: "",
    City: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const itemsPerPageOptions = [25, 50, 100, 300, 400, totalItems];

  const isDesktop = useMediaQuery({ minWidth: 767 });
  const dispatch = useDispatch();
  const maxLength = 9;

  // Get user permissions and roles from Redux state
  const { userPermissions, loadingPermissions } = useSelector(
    (state) => state.users
  );

  // Helper function to remove empty values from object
  const removeEmptyValues = (obj) => {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
      if (
        cleaned[key] === "" ||
        cleaned[key] === null ||
        cleaned[key] === undefined
      ) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Helper function to build search parameters
  const buildSearchParams = (term) => {
    if (!term.trim()) return {};

    // Check if the search term is a phone number (contains only digits, +, or spaces)
    const isPhoneNumber = /^[\d+\s]+$/.test(term);

    // Check if the search term is a name (contains Arabic/English letters)
    const isName = /[\u0600-\u06FFa-zA-Z]/.test(term);

    const searchParams = {};

    if (isPhoneNumber) {
      // If it looks like a phone number, search only by phone
      searchParams.PhoneNumber = term.replace(/\s/g, "");
    } else if (isName) {
      // If it looks like a name, search only by name
      searchParams.CustomerName = term;
    } else {
      // For mixed or unclear input, search by both but let backend handle it
      searchParams.CustomerName = term;
      searchParams.PhoneNumber = term;
    }

    return searchParams;
  };

  // Remove item from display data after successful action
  const removeItemFromDisplayData = (requestId) => {
    setDisplayData((prevData) =>
      prevData.filter((item) => item.RequestId !== requestId)
    );
    setTotalItems((prevTotal) => prevTotal - 1);
  };

  const getAllAppointments = async (
    currentPage = 1,
    pageSize = itemsPerPageOptions,
    searchParams = {},
    filterParams = {}
  ) => {
    try {
      setLoading(true);

      const technicianId = localStorage.getItem("UserId");
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);

      const url = `https://api.generalhome.sa/api/maintenance/GetApprovedRequests`;

      // Step 1️⃣: Initial payload to get total items count
      const initialPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: currentPage,
        PageSize: pageSize,
        ...searchParams,
        ...filterParams,
      });

      const firstRes = await axios.post(url, initialPayload);
      const totalItemsFromAPI = firstRes.data.TotalItems || 0;

      // Step 2️⃣: If there are more items than the default page size, fetch them all
      const fullPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: 1, // always first page when showing all items
        PageSize: totalItemsFromAPI > 0 ? totalItemsFromAPI : pageSize,
        ...searchParams,
        ...filterParams,
      });

      const res =
        totalItemsFromAPI > pageSize
          ? await axios.post(url, fullPayload)
          : firstRes;

      // Handle API response
      const totalPages = res.data.TotalPages || 1;
      const totalNumberOfPages =
        totalPages ||
        Math.ceil((res.data.TotalItems || 0) / fullPayload.PageSize);

      setPageCount(totalNumberOfPages);
      setDisplayData(res.data.Items);
      setTotalItems(res.data.TotalItems);
      setCurrentPage(0);
    } catch (err) {
      setError(err.message);
      setDisplayData([]);
      setTotalItems(0);
      setPageCount(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  };

  const getApprovedTechnicians = async () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds =
        maintenanceAreaIdsString?.split(",").map(Number) || [];

      const url = `https://api.generalhome.sa/api/maintenance/GetApprovedTechnicians`;
      const res = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
      });
      setTechnicians(res.data);
    } catch (err) {
      console.error("Error fetching technicians:", err);
    }
  };

  const handleMoveData = async (item, index) => {
    const selectedDropdownOption = document.querySelector(
      `#tech-dropdown-${index}`
    ).value;
    const matchingItem = displayData.find(
      (el) => el.RequestId === item.RequestId
    );
    if (
      (matchingItem && matchingItem.IsDateModified) ||
      selectedDropdownOption !== "none"
    ) {
      let editedDate = "";
      if (matchingItem && matchingItem.IsDateModified) {
        editedDate = matchingItem.MaintenanceStartDate;
      }
      try {
        const parsedData = JSON.parse(JSON.stringify(item));
        const requestId = parsedData.RequestId;
        await axios.post(
          "https://api.generalhome.sa/api/maintenance/AssigningMaintananceStartDateAndTechnicians",
          {
            RequestId: requestId,
            MaintenanceStartDate: editedDate,
            SelectedTechnicianId: selectedDropdownOption,
          }
        );
        toast.success("تم جدوله الطلب بنجاح", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        // ✅ Remove the item from display data instead of refetching
        removeItemFromDisplayData(requestId);
      } catch (error) {
        console.error(error);
      }
    } else {
      toast.warning("لم يتم إجراء أي تغييرات.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const deleteRequest = async (item) => {
    Swal.fire({
      title: "هل أنت متأكد",
      text: "لن تستطيع استعاده الطلب !",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      backdrop: `rgba(0,0,123,0.4)`,
      customClass: {
        container: "my-swal",
      },
    }).then(async (willDelete) => {
      if (willDelete.value) {
        try {
          const parsedData = JSON.parse(JSON.stringify(item));
          const requestId = parsedData.RequestId;
          await axios.delete(
            "https://api.generalhome.sa/api/maintenance/DeleteMaintenanceRequest",
            {
              params: {
                requestId: requestId,
              },
            }
          );
          Swal.fire({
            title: "Deleted!",
            text: "The AC type has been deleted successfully",
            icon: "success",
            confirmButtonText: "Cool",
            timer: 2000,
          });
          // ✅ Remove the item from display data instead of refetching
          removeItemFromDisplayData(requestId);
        } catch (error) {
          let errorMessage;
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            errorMessage = error.response.data.message;
            console.log(errorMessage);
          } else {
            errorMessage = "An unexpected error occurred";
            console.log(errorMessage);
          }
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "Try Again",
          });
          setError(error.response?.data || {});
        }
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your AC type is safe :)",
          icon: "info",
          confirmButtonText: "OK",
        });
      }
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  // Handle search submit (when user clicks search button or presses enter)
  const handleSearchSubmit = () => {
    const searchParams = buildSearchParams(searchTerm);
    getAllAppointments(1, itemsPerPage, searchParams, filters);
  };

  // Handle enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Reset to first page when applying filters
    getAllAppointments(1, itemsPerPage, buildSearchParams(searchTerm), filters);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      MaintenanceTypeId: "",
      City: "",
    });
    setSearchTerm(""); // Clear search term when resetting filters
    // Reset to first page and fetch without filters
    getAllAppointments(1, itemsPerPage);
  };

  // Pagination click
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    getAllAppointments(
      pageNumber,
      itemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    getAllAppointments(
      1,
      newItemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const handleTogglePopup = (item) => {
    if (notePopUpRequestId === item.RequestId) {
      setNotePopUpRequestId(null);
    } else {
      setNotePopUpRequestId(item.RequestId);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("UserId");
    if (userId) {
      dispatch(fetchUserPermissions(userId));
    } else {
      console.error("User ID not found in local storage");
    }

    getAllAppointments(1, itemsPerPage);
    getApprovedTechnicians();
  }, [dispatch]);

  const getWarrantyStatus = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    const firstStatus = maintenanceDetails[0].WarrantyStatusName;
    const allSame = maintenanceDetails.every(
      (item) => item.WarrantyStatusName === firstStatus
    );
    return allSame ? firstStatus : "مختلط";
  };

  const CanWriteWaitingRequests =
    String(userPermissions?.CanWriteWaitingRequests).toLowerCase() === "true";

  return (
    <>
      <MobileNav />
      <section className="waitingList">
        <Helmet>
          <meta charSet="utf-8" />
          <title>قائمة الإنتظار</title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/approvesList"
          />
        </Helmet>
        {loading || loadingPermissions ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading || loadingPermissions}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : (
          <>
            {isDesktop ? (
              <div className="newAppointment-container" ref={componentRef}>
                <PrintingHeader title="قائمة الإنتظار" count={totalItems} />
                <SearchInput
                  searchTerm={searchTerm}
                  handleSearch={handleSearchChange}
                  handleSearchSubmit={handleSearchSubmit}
                  handleKeyPress={handleKeyPress}
                  setSearchTerm={setSearchTerm}
                  placeholder="ابحث عن اسم العميل أو رقم الجوال"
                />

                <div className="info d-flex justify-content-between">
                  <div className="info-header">
                    <h2>قائمة الإنتظار</h2>
                    <h3>{totalItems}</h3>
                  </div>
                  <div className="print-icon-holder">
                    <img
                      src={printIcon}
                      alt="printIcon"
                      className="print-icon"
                      onClick={handlePrint}
                    />
                  </div>
                </div>

                {/* Filter form */}
                <div className="form-holder">
                  <h5>ما الذي تبحث عنه؟</h5>
                  <form
                    className="mt-4 filter-form"
                    onSubmit={handleFilterSubmit}
                  >
                    <div className="filter-row">
                      <div className="filter-group">
                        <select
                          name="MaintenanceTypeId"
                          value={filters.MaintenanceTypeId}
                          onChange={handleFilterChange}
                        >
                          <option value="">اختر نوع الخدمة</option>
                          <option value="1">صيانه</option>
                          <option value="2">تنظيف</option>
                        </select>
                      </div>
                      <div className="filter-group">
                        <input
                          type="text"
                          name="City"
                          placeholder="ابحث بالمدينة"
                          value={filters.City}
                          onChange={handleFilterChange}
                        />
                      </div>
                    </div>
                    <div className="filter-button-container">
                      <button
                        type="button"
                        className="reset-button"
                        onClick={handleResetFilters}
                        disabled={loading}
                      >
                        إعادة تعيين
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? "جاري التصفية..." : "تصفية"}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="table-responsive-sm overflow-auto">
                  {displayData?.length > 0 ? (
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th scope="row">إسم العميل</th>
                          <th scope="row">رقم الهاتف</th>
                          <th scope="row">المدينه</th>
                          <th scope="row">الحي</th>
                          <th scope="row">الملاحظات</th>
                          <th scope="row">تاريخ الصيانه</th>
                          <th scope="row">الفني</th>
                          <th className="text-center" scope="row">
                            التحكم
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayData.map((item, index) => (
                          <tr key={item.RequestId}>
                            <td
                              style={{
                                position: "relative",
                                padding: 0,
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                              }}
                            >
                              <div
                                style={
                                  item.MaintenanceTypeId === 2
                                    ? {
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                        borderTopRightRadius: "8px",
                                        borderBottomRightRadius: "8px",
                                        borderRight: "4px solid #007bff",
                                        padding: "8px",
                                        display: "flex",
                                      }
                                    : {
                                        padding: "8px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                      }
                                }
                              >
                                {item.CustomerName &&
                                item.CustomerName.length > maxLength
                                  ? `${item.CustomerName.substring(
                                      0,
                                      maxLength
                                    )}...`
                                  : item.CustomerName}
                              </div>
                            </td>

                            <td>
                              <a
                                href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                                  /[^\d+]/g,
                                  ""
                                )}&text=Hello%2C%20I'm%20interested%20in%20your%20product`}
                                target="__blank"
                                rel="noopener noreferrer"
                              >
                                {item.PhoneNumber}
                              </a>
                            </td>

                            <td>{item.City}</td>

                            <td>
                              {item.Neighborhood &&
                              item.Neighborhood.length > maxLength
                                ? `${item.Neighborhood.substring(
                                    0,
                                    maxLength
                                  )}...`
                                : item.Neighborhood}
                            </td>

                            <td>
                              {item.ErrorType?.trim()
                                ? item.ErrorType.length > maxLength
                                  ? `${item.ErrorType.substring(
                                      0,
                                      maxLength
                                    )}...`
                                  : item.ErrorType
                                : "No note"}

                              {item.ErrorType?.trim() &&
                                item.ErrorType.length > maxLength && (
                                  <span
                                    onClick={() => handleTogglePopup(item)}
                                    className="see-more-link"
                                  >
                                    المزيد
                                  </span>
                                )}

                              {item.ErrorType?.trim() &&
                                notePopUpRequestId === item.RequestId && (
                                  <div className="notePopup">
                                    <div className="notePopup-content">
                                      <button
                                        onClick={() => handleTogglePopup(item)}
                                        className="close-btn d-flex"
                                      >
                                        X
                                      </button>
                                      <h3>الملاحظات</h3>
                                      <p>{item.ErrorType}</p>
                                    </div>
                                  </div>
                                )}
                            </td>

                            <td>
                              {item.IsDateModified ? (
                                <input
                                  type="date"
                                  min={new Date().toISOString().slice(0, 16)}
                                  value={item.MaintenanceStartDate || ""}
                                  onChange={(event) => {
                                    setDisplayData(
                                      displayData.map((el) =>
                                        el.RequestId === item.RequestId
                                          ? {
                                              ...el,
                                              MaintenanceStartDate:
                                                event.target.value,
                                              IsDateModified: true,
                                            }
                                          : el
                                      )
                                    );
                                  }}
                                />
                              ) : (
                                <p>{item.MaintenanceStartDate}</p>
                              )}
                            </td>

                            {technicians?.length > 0 ? (
                              <td>
                                <div className="selection-box mt-0">
                                  <select
                                    className="custom-select w-100"
                                    id={`tech-dropdown-${index}`}
                                  >
                                    <option value="none">تحديد الفني</option>
                                    {technicians.map((tech) => (
                                      <option
                                        value={tech.TechnicianId}
                                        key={tech.TechnicianId}
                                      >
                                        {tech.Username}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </td>
                            ) : (
                              <td>لا يوجد فنيون</td>
                            )}

                            <td className="waitingBtn">
                              <button
                                disabled={!CanWriteWaitingRequests}
                                onClick={() => {
                                  setDisplayData(
                                    displayData.map((el) =>
                                      el.RequestId === item.RequestId
                                        ? {
                                            ...el,
                                            IsDateModified: !el.IsDateModified,
                                          }
                                        : el
                                    )
                                  );
                                }}
                              >
                                التاريخ
                              </button>

                              <button
                                disabled={!CanWriteWaitingRequests}
                                onClick={() => handleMoveData(item, index)}
                                className="btn btn-approve"
                              >
                                جدوله
                              </button>

                              <Link
                                to={`/dashboard/inProgress/${item.RequestId}`}
                              >
                                <button>تفاصيل</button>
                              </Link>

                              <button
                                disabled={!CanWriteWaitingRequests}
                                className="btn btn-danger"
                                onClick={() => deleteRequest(item)}
                              >
                                مسح
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    // ✅ Show this instead of the entire table when there's no data
                    <div className="no-data-card">
                      <div className="no-data-content">
                        <div className="no-data-icon">📭</div>
                        <h3>لا توجد طلبات</h3>
                        <p>لا توجد بيانات لعرضها حالياً</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Mobile view
              <div className="waitingListMob-container" ref={componentRef}>
                <PrintingHeader title="قائمة الإنتظار" count={totalItems} />
                <SearchInput
                  searchTerm={searchTerm}
                  handleSearch={handleSearchChange}
                  handleSearchSubmit={handleSearchSubmit}
                  handleKeyPress={handleKeyPress}
                  setSearchTerm={setSearchTerm}
                  placeholder="ابحث عن اسم العميل أو رقم الجوال"
                />

                <div className="info d-flex justify-content-between">
                  <div className="info-header">
                    <h2>قائمة الإنتظار</h2>
                    <h3>{totalItems}</h3>
                  </div>
                  <div className="print-icon-holder">
                    <img
                      src={printIcon}
                      alt="printIcon"
                      className="print-icon"
                      onClick={handlePrint}
                    />
                  </div>
                </div>

                {/* Mobile filter form */}
                <div className="form-holder">
                  <h5>ما الذي تبحث عنه؟</h5>
                  <form
                    className="mt-4 filter-form"
                    onSubmit={handleFilterSubmit}
                  >
                    <div className="filter-group">
                      <select
                        name="MaintenanceTypeId"
                        value={filters.MaintenanceTypeId}
                        onChange={handleFilterChange}
                      >
                        <option value="">اختر نوع الخدمة</option>
                        <option value="1">صيانه</option>
                        <option value="2">تنظيف</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <input
                        type="text"
                        name="City"
                        placeholder="ابحث بالمدينة"
                        value={filters.City}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="filter-button-container">
                      <button
                        type="button"
                        className="reset-button"
                        onClick={handleResetFilters}
                        disabled={loading}
                      >
                        إعادة تعيين
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? "جاري التصفية..." : "تصفية"}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="beautiful-cards-container">
                  {displayData?.length > 0 ? (
                    displayData.map((item, index) => (
                      <div
                        key={item.RequestId}
                        className={`beautiful-card ${
                          item.MaintenanceTypeId === 2
                            ? "cleaning"
                            : "maintenance"
                        }`}
                      >
                        {/* Customer Info Section */}
                        <div className="card-section customer-section">
                          <div className="section-header">
                            <FaUserCog className="section-icon" />
                            <span>معلومات العميل</span>
                          </div>
                          <div className="customer-info">
                            <div className="info-item">
                              <span className="info-label">الاسم:</span>
                              <span className="info-value highlight">
                                {item.CustomerName}
                              </span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">الهاتف:</span>
                              <a
                                href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                                  /[^\d+]/g,
                                  ""
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-link"
                              >
                                <FaWhatsapp className="whatsapp-icon" />
                                {item.PhoneNumber}
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Location Section */}
                        <div className="card-section location-section">
                          <div className="section-header">
                            <FaMapMarkerAlt className="section-icon" />
                            <span>الموقع</span>
                          </div>
                          <div className="location-info">
                            <div className="info-item">
                              <span className="info-label">المدينة:</span>
                              <span className="info-value">{item.City}</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">الحي:</span>
                              <span className="info-value">
                                {item.Neighborhood}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Notes Section */}
                        <div className="card-section notes-section">
                          <div className="section-header">
                            <FaInfoCircle className="section-icon" />
                            <span>الملاحظات</span>
                          </div>
                          <div className="notes-content">
                            <div className="notes-text">{item.ErrorType}</div>
                          </div>
                        </div>

                        {/* Date & Technician Section */}
                        <div className="card-section scheduling-section">
                          <div className="section-header">
                            <FaCalendarAlt className="section-icon" />
                            <span>الجدولة</span>
                          </div>

                          <div className="scheduling-content">
                            <div className="date-section">
                              <label>تاريخ الصيانه:</label>
                              {item.IsDateModified ? (
                                <input
                                  type="date"
                                  min={new Date().toISOString().slice(0, 16)}
                                  value={item.MaintenanceStartDate || ""}
                                  onChange={(event) => {
                                    setDisplayData(
                                      displayData.map((el) =>
                                        el.RequestId === item.RequestId
                                          ? {
                                              ...el,
                                              MaintenanceStartDate:
                                                event.target.value,
                                              IsDateModified: true,
                                            }
                                          : el
                                      )
                                    );
                                  }}
                                  className="date-picker"
                                />
                              ) : (
                                <div className="date-display">
                                  {item.MaintenanceStartDate}
                                </div>
                              )}
                            </div>

                            <div className="technician-section">
                              <label>اختر الفني:</label>
                              {technicians?.length > 0 ? (
                                <select
                                  className="technician-select"
                                  id={`tech-dropdown-${index}`}
                                >
                                  <option value="none">تحديد الفني</option>
                                  {technicians.map((tech) => (
                                    <option
                                      value={tech.TechnicianId}
                                      key={tech.TechnicianId}
                                    >
                                      {tech.Username}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="no-tech-message">
                                  لا يوجد فنيون متاحون
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="card-actions">
                          <div className="action-buttons">
                            <button
                              onClick={() => {
                                setDisplayData(
                                  displayData.map((el) =>
                                    el.RequestId === item.RequestId
                                      ? {
                                          ...el,
                                          IsDateModified: !el.IsDateModified,
                                        }
                                      : el
                                  )
                                );
                              }}
                              disabled={!CanWriteWaitingRequests}
                              className="action-btn date-btn"
                            >
                              <FaCalendarAlt />
                              تعديل التاريخ
                            </button>

                            <button
                              disabled={!CanWriteWaitingRequests}
                              onClick={() => {
                                handleMoveData(item, index);
                              }}
                              className="action-btn schedule-btn"
                            >
                              <FaClock />
                              جدولة
                            </button>

                            <Link
                              to={`/dashboard/inProgress/${item.RequestId}`}
                              className="action-link"
                            >
                              <button className="action-btn details-btn">
                                <FaInfoCircle />
                                التفاصيل
                              </button>
                            </Link>

                            <button
                              onClick={() => deleteRequest(item)}
                              disabled={!CanWriteWaitingRequests}
                              className="action-btn delete-btn"
                            >
                              <FaTrash />
                              حذف
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-data-card">
                      <div className="no-data-content">
                        <div className="no-data-icon">📭</div>
                        <h3>لا توجد طلبات في قائمة الانتظار</h3>
                        <p>لا توجد بيانات لعرضها حالياً</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default ApprovedList;
